import * as _ from 'lodash';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { DropdownButton, MenuItem, Modal } from 'react-bootstrap';
import Toggle from 'react-toggle';

import { BannerStyle } from '../../../../../typings/enums';
import { withTargetChecked } from '../../../../utils';
import { icons } from '../../../generic';
import Button, { ButtonGroup } from '../../../generic/Button';
import { CharacterCount, FormGroup, FormLabel, LabeledTextInput } from '../../../generic/Forms';
import QuillEditor from '../../../generic/QuillEditor';
import type { CreateBannerRequest } from '../../../../../typings/api';

import type { State as AddEditState, UseAddEditBanner } from './useAddEditBanner';

const bannerDuration = (startDate: string, endDate: string | null) => {
  if (!endDate) {
    return 'Ongoing';
  }

  const start = moment(startDate);
  const end = moment(endDate);
  if (start.isAfter(end)) {
    // Invalid inputs.
    return <span className="italic">Invalid</span>;
  }

  // A second is added to the end date to ensure that a day is counted from the start to the end of a single day.
  // The "humanize" format of moment.js is not precise enough, and simply appending "# months" to "# days" when the
  // duration is more than a month doesn't work either because of the way moment.js converts a duration to days.
  return moment.duration(end.add(1, 'second').diff(start)).format();
};

const dateInputFormat = 'YYYY-MM-DD';

const bannerStyleToLabelMap = {
  [BannerStyle.Low]: <span>Blue (default)</span>,
  [BannerStyle.Medium]: <span>Yellow</span>,
  [BannerStyle.High]: <span>Red</span>,
};

// bannerDataIsValid makes some basic checks that the inputs to create or update a banner are valid.
const bannerDataIsValid = (banner: Omit<CreateBannerRequest, 'body'>): boolean => {
  if (!banner.title) {
    return false;
  }

  if (banner.endDate) {
    return moment(banner.endDate).isAfter(moment(banner.startDate));
  }

  return true;
};

type ModalAddEditBannerProps = Omit<UseAddEditBanner, 'state' | 'onAddNewBanner' | 'onEditBanner' | 'onDuplicateBanner'> & {
  state: AddEditState;
  isCreatingOrUpdating: boolean;
  onSubmitAddEdit: () => void;
};

/**
 * Banner for creating a new banner or editing an existing one.
 *
 * @param {ModalAddEditBannerProps} props - Component props.
 * @param {State} props.state - Banner adding/editing state.
 * @param {boolean} props.isEdited - Whether the form has been edited since being opened.
 * @param {boolean} props.isCreatingOrUpdating - Whether the form is creating or updating a banner.
 * @param {() => void} props.onCloseModal - Handle closing the modal
 * @param {TextInputOnChange} props.onTitleChange - Handle title input change.
 * @param {TextInputOnChange} props.onBodyChange - Handle body textarea change.
 * @param {(startDate: Date) => void} props.onStartDateChange - Handle startDate change.
 * @param {(endDate: Date | null) => void} props.onEndDateChange - Handle end date change.
 * @param {(isDismissible: boolean) => void} props.onToggleIsDismissible - Handle toggling the isDismissible checkbox.
 * @param {(bannerStyle: BannerStyle) => void} props.onBannerStyleChange - Handle changing the banner style.
 * @param {() => void} props.onSubmitAddEdit - Handle form submission.
 */
const ModalAddEditBanner = ({
  state,
  isEdited,
  isCreatingOrUpdating,
  onCloseModal,
  onTitleChange,
  onBodyChange,
  onStartDateChange,
  onEndDateChange,
  onToggleIsDismissible,
  onBannerStyleChange,
  onSubmitAddEdit,
}: ModalAddEditBannerProps) => (
  <Modal
    onHide={onCloseModal}
    show
    className="banner-settings__add-edit-modal"
    bsSize="large"
  >
    <form onSubmit={(event) => {
      event.preventDefault();
      onSubmitAddEdit();
    }}>
      <header className="banner-settings__add-edit-modal__header">
        <div className="banner-settings__add-edit-modal__heading">
          Add Notification banner
        </div>
        <div className="banner-settings__add-edit-modal__title">
          {state.mode === 'edit'
            ? state.originalTitle
            : (state.banner.title || <span className="banner-settings__add-edit-modal__untitled">(untitled)</span>)}
        </div>
      </header>
      <Modal.Body>
        <hr />
        <FormGroup
          legend="Content"
          alignLegend="left"
          direction="column"
          spacing="sm"
        >
          <LabeledTextInput
            labelVariant="legacy"
            label="Title"
            placeholder="A title for your banner"
            onChange={onTitleChange}
            value={state.banner.title}
            maxLength={50}
            showCharCount
            isRequired
          />
          <div>
            {/* We are not wrapping the QuillEditor in the label, as it forces all text to be bold. */}
            <FormLabel variant="legacy" label="Body" requiredText="(Required)" />
            <QuillEditor
              totalHeight={180}
              initialContents={state.banner.body.contents}
              quillOptions={{
                modules: {
                  toolbar: ['bold', 'italic', 'link'],
                },
                placeholder: 'Some text for your banner',
              }}
              maxLength={250}
              onChange={onBodyChange}
            />
            <CharacterCount
              text="Characters: "
              maxLength={250}
              currentLength={state.banner.body.length}
            />
          </div>
        </FormGroup>
        <hr />
        <FormGroup
          legend="Schedule"
          alignLegend="left"
          direction="row"
          spacing="sm"
        >
          <FormLabel
            variant="legacy"
            label="Start Date"
          >
            <input
              type="date"
              value={moment(state.banner.startDate).format(dateInputFormat)}
              onChange={event => onStartDateChange(moment(event.target.value).startOf('day').toISOString())}
              min={moment().format(dateInputFormat)}
            />
            <div className="banner-settings__add-edit-modal__date-input-required">Required</div>
          </FormLabel>
          <FormLabel
            variant="legacy"
            label="End Date"
          >
            <input
              type="date"
              value={state.banner.endDate ? moment(state.banner.endDate).format(dateInputFormat) : ''}
              onChange={event => onEndDateChange(moment(event.target.value).endOf('day').toISOString())}
              min={moment().format(dateInputFormat)}
            />
            {state.banner.endDate && (
              <button
                type="button"
                className="banner-settings__add-edit-modal__date-input-clear"
                onClick={() => {
                  onEndDateChange(null);
                }}
              >
                Clear
              </button>
            )}
          </FormLabel>
          <div className="banner-settings__add-edit-modal__duration">
            Duration:<br />{bannerDuration(state.banner.startDate, state.banner.endDate)}
          </div>
        </FormGroup>
        <hr />
        <FormGroup
          legend="Configuration"
          alignLegend="left"
          direction="row"
          spacing="sm"
        >
          <FormLabel
            variant="legacy"
            label="Style"
          >
            <DropdownButton
              id="banner-style-dropdown"
              title={bannerStyleToLabelMap[state.banner.style]}
              onSelect={onBannerStyleChange}
            >
              {Object.entries(bannerStyleToLabelMap).map(([key, label]) => (
                <MenuItem
                  key={key}
                  active={state.banner.style === key}
                  eventKey={key}
                >
                  {label}
                </MenuItem>
              ))}
            </DropdownButton>
          </FormLabel>
          <FormLabel
            variant="legacy"
            label="Dismissible"
          >
            <Toggle
              id="banner-dismissible-toggle"
              checked={state.banner.isDismissible}
              onChange={withTargetChecked(onToggleIsDismissible)}
              icons={false}
            />
          </FormLabel>
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={onCloseModal}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            onClick={_.noop /* Submission is handled at the form level. */}
            isDisabled={
              // If the form hasn't been edited but the banner is deactivated, allow submission to activate it.
              (!isEdited && (state.mode !== 'edit' || !state.banner.isDeactivated)) ||
              isCreatingOrUpdating ||
              !bannerDataIsValid(state.banner)
            }
            icon={isCreatingOrUpdating ? icons.circleO : undefined}
            isSubmitButton
          >
            Submit{state.mode === 'edit' && state.banner.isDeactivated && ' (activate)'}
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </form>
  </Modal>
);

export default ModalAddEditBanner;
